import { CLINICAL_PHASES, type ClinicalPhase } from "@care-by-ella/contracts";

// TES-841 — the doctor's clinical phase choice, on BOTH decision surfaces
// (Emelie 2026-09-22): "På båda adminytorna – efter vårdmöte och vid asynkron"
// "F-07-granskning – måste Kirill välja klinisk behandlingsfas innan ett"
// "positivt behandlingsbeslut kan publiceras." One component, for the same
// reason PrescriptionOrderFields is one: two copies of the label pair are two
// chances for the two surfaces to name the same clinical decision differently.
//
// Swedish from Emelie's own wording: ACTIVE_TREATMENT = "Aktiv behandling",
// WEIGHT_STABILISATION = "Viktstabilisering". The second label used to read
// "Underhållsfas", which is the word she is replacing — and the same word as
// the "Ella Underhåll" subscription, a conflation she rules out by name.
export const CLINICAL_PHASE_LABEL: Record<ClinicalPhase, string> = {
  active_treatment: "Aktiv behandling",
  weight_stabilisation: "Viktstabilisering",
};

// The route each phase puts the patient on, so the doctor is choosing between
// two described outcomes rather than between two nouns (TES-840 punkt 4).
const CLINICAL_PHASE_HINT: Record<ClinicalPhase, string> = {
  active_treatment:
    "Patienten går vidare till CP-12, registrerar sin första dos och får dagens tips från det datumet.",
  weight_stabilisation:
    "Patienten går vidare till CP-61. Ingen första dos registreras och ingen dagräknare startar.",
};

/**
 * `value` is `null` until the doctor picks — there is no preselected phase.
 *
 * That empty first option is the whole point of the component: "Inget"
 * "defaultvärde", and a select that opens on "Aktiv behandling" records a
 * choice from every doctor who never looked at the field. The API refuses a
 * decision without one, so the only thing a default would buy is a phase
 * nobody chose, arriving as though someone had.
 */
export function ClinicalPhaseField({
  value,
  onChange,
  disabled,
  idPrefix,
  hint,
  emptyOption,
  emptyHint,
}: {
  value: ClinicalPhase | null;
  onChange: (phase: ClinicalPhase | null) => void;
  disabled?: boolean;
  idPrefix: string;
  // TES-1011 — the monthly review asks the same question about a patient
  // already in treatment, so the route each choice means differs from the
  // first decision's. Absent: the first decision's hints and wording.
  hint?: (phase: ClinicalPhase) => string;
  emptyOption?: string;
  emptyHint?: string;
}) {
  const id = `${idPrefix}-clinical-phase`;
  return (
    <label className="flex flex-col gap-xs text-sm" htmlFor={id}>
      <span className="text-textSecondary">Klinisk behandlingsfas</span>
      <select
        id={id}
        className="border border-surfaceMuted rounded-md p-sm text-sm"
        value={value ?? ""}
        onChange={(e) =>
          onChange(e.target.value === "" ? null : (e.target.value as ClinicalPhase))
        }
        disabled={disabled}
      >
        <option value="">{emptyOption ?? "Välj behandlingsfas…"}</option>
        {CLINICAL_PHASES.map((p) => (
          <option key={p} value={p}>
            {CLINICAL_PHASE_LABEL[p]}
          </option>
        ))}
      </select>
      {value ? (
        <span className="text-xs text-textSecondary">
          {hint ? hint(value) : CLINICAL_PHASE_HINT[value]}
        </span>
      ) : (
        <span className="text-xs text-textSecondary">
          {emptyHint ??
            "Fasen avgör patientens fortsatta väg och måste väljas innan beslutet publiceras."}
        </span>
      )}
    </label>
  );
}
